import React, { useState, useEffect, useCallback } from 'react';
import { isDeliveryType, isPickupType } from '../../../constants/bookingTypes';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tooltip } from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUsers, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import './bookings.css';
import { isMaintenanceTypeName } from './bookingTypeRules';
import { colorOf } from '../../../constants/areas';

// What a maintenance-like card says instead of "Cleaners: 0" (owner,
// 2026-10-04): the office should see at a glance that no cleaners are
// needed, especially on a pending card.
const maintenanceCardLabel = (typeName) => (isMaintenanceTypeName(typeName)
  ? (typeName === 'Swimming Pool Maintenance' ? 'Pool Maintenance' : 'Maintenance')
  : null);

const DUBAI = 'Asia/Dubai';

function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(DateTime.now().setZone(DUBAI));
  const [showPastDays, setShowPastDays] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const today = DateTime.now().setZone(DUBAI);

  const fetchBookings = useCallback(async (weekStart) => {
    try {
      const date = weekStart.toFormat('yyyy-MM-dd');
      const response = await axios.get(`/api/bookings/week/${date}`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching weekly bookings:', error);
    }
  }, []);

  useEffect(() => {
    fetchBookings(currentWeek);
  }, [currentWeek, fetchBookings]);

  const getMonday = (date) => date.setZone(DUBAI).startOf('week');

  const getCurrentWeekDays = () => {
    const startOfWeek = getMonday(currentWeek);
    return Array.from({ length: 7 }, (_, i) => startOfWeek.plus({ days: i }));
  };

  const changeWeek = (direction) => {
    setCurrentWeek(currentWeek.plus({ weeks: direction }));
  };

  const getAreaColor = (area) => {

    return colorOf(area);
  };

  const getBusiestHourSummary = (dayBookings) => {
    const timeSlots = {};
    dayBookings.forEach(({ booking_start, booking_end, required_cleaners }) => {
      const startHour = DateTime.fromISO(booking_start, { zone: DUBAI }).hour;
      const endHour = DateTime.fromISO(booking_end, { zone: DUBAI }).hour;
      for (let hour = startHour; hour < endHour; hour++) {
        const slot = `${hour}:00 - ${hour + 1}:00`;
        timeSlots[slot] = (timeSlots[slot] || 0) + required_cleaners;
      }
    });
    return Object.entries(timeSlots).reduce((max, entry) =>
      entry[1] > max.maxCleaners ? { busiestSlot: entry[0], maxCleaners: entry[1] } : max
      , { busiestSlot: null, maxCleaners: 0 });
  };

  const isCurrentWeek = () => {
    const todayWeekStart = getMonday(today);
    const currentWeekStart = getMonday(currentWeek);

    // Same week if their "Monday" (startOf('week')) is the same calendar day
    return todayWeekStart.toISODate() === currentWeekStart.toISODate();
  };

  const filterBySearch = (booking) => {
    const { building_name = '', unit_number = '', client_name = '' } = booking;
    const term = searchTerm.toLowerCase();
    return building_name.toLowerCase().includes(term)
      || unit_number.toLowerCase().includes(term)
      || client_name.toLowerCase().includes(term);
  };

  const weekDays = getCurrentWeekDays();

  return (
    <div className="bookings-container">
      <div className="top-actions">
        <button className="add-booking-button" onClick={() => navigate('/bookings/new')}>Add New Booking</button>
        <button className="add-booking-button" onClick={() => navigate('/bookings/summary-report')}>Booking Summary</button>
      </div>

      <div className="top-actions">
        <div className='left-actions'>
          <div className="bookings-search-bar-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input type="text" placeholder="Search by Building, Unit, or Client..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-bar" />
          </div>
        </div>

        <div className="navigation">
          {isCurrentWeek() && (
            <button className="show-past-days-button" onClick={() => setShowPastDays(prev => !prev)}>
              {showPastDays ? 'Hide Earlier Days' : 'Show Earlier Days'}
            </button>
          )}
          <div className='week-navigation-buttons'>
            <button className="previous-week-button" onClick={() => changeWeek(-1)}>Previous Week</button>
            <button className="next-week-button" onClick={() => changeWeek(1)}>Next Week</button>
          </div>
        </div>
      </div>

      <div className="bookings-grid">
        {weekDays.map((day, index) => {
          const dayStart = day.startOf('day');
          const dayEnd = day.endOf('day');
          const formattedDate = dayStart.toFormat('yyyy-MM-dd');

          if (isCurrentWeek() && !showPastDays && dayStart < today.startOf('day')) return null;

          const dayBookings = bookings.filter(b => {
            const bookingDate = DateTime.fromISO(b.booking_start, { zone: DUBAI });
            return bookingDate >= dayStart && bookingDate <= dayEnd;
          });

          const filteredBookings = dayBookings.filter(filterBySearch);

          const pending = filteredBookings.filter(b => b.status_name === 'Pending');
          const cancelled = filteredBookings.filter(b => b.status_name === 'Last Minute Cancelled');
          const rescheduled = filteredBookings.filter(b => b.status_name === 'Last Minute Rescheduled');
          // A booking sits in ONE section: the status sections above win, the
          // type sections take what is left (a pending pool booking used to
          // appear twice, as "Pending 1" and "Swimming Pool Maintenance 1").
          const byType = filteredBookings.filter(
            (b) => !['Pending', 'Last Minute Cancelled', 'Last Minute Rescheduled'].includes(b.status_name)
          );
          const laundry = byType.filter(b => b.booking_type === 'Laundry Service Only');
          const delivery = byType.filter(b => isDeliveryType(b.booking_type));
          const porter = byType.filter(b => b.booking_type === 'Porter Delivery');
          const pickup = byType.filter(b => isPickupType(b.booking_type));
          const maintenance = byType.filter(b => b.booking_type === 'Maintenance');
          const pool = byType.filter(b => b.booking_type === 'Swimming Pool Maintenance');

          // "Other" should exclude Pending by STATUS, not TYPE
          const other = filteredBookings.filter(
            (b) =>
              !['Laundry Service Only', 'Delivery', 'Pickup', 'Porter Delivery', 'Maintenance', 'Swimming Pool Maintenance'].includes(b.booking_type) &&
              !['Pending', 'Last Minute Cancelled', 'Last Minute Rescheduled'].includes(b.status_name)
          );

          const sections = [
            { data: pending, label: 'Pending', className: 'pending-booking', badge: 'Pending' },
            { data: other, label: 'Booking', className: '', badge: null },
            { data: laundry, label: 'Laundry', className: 'laundry-booking', badge: 'Laundry Only' },
            { data: delivery, label: 'Delivery', className: 'delivery-booking', badge: 'Delivery' },
            { data: pickup, label: 'Pickup', className: 'pickup-booking', badge: 'Pickup' },
            { data: porter, label: 'Porter Delivery', className: 'delivery-booking', badge: 'Porter Delivery' },
            { data: maintenance, label: 'Maintenance', className: 'maintenance-booking', badge: 'Maintenance' },
            { data: pool, label: 'Swimming Pool Maintenance', className: 'maintenance-booking', badge: 'Swimming Pool Maintenance' },
            { data: [...cancelled, ...rescheduled], label: 'Cancellation', className: 'cancelled-rescheduled-booking', badge: null }
          ];


          const summaryBookings = dayBookings.filter(
            (b) =>
              ['Confirmed', 'In Process', 'Completed'].includes(b.status_name) &&
              !['Laundry Service Only', 'Delivery', 'Pickup', 'Porter Delivery', 'Maintenance', 'Swimming Pool Maintenance'].includes(b.booking_type)
          );
          const { maxCleaners } = getBusiestHourSummary(summaryBookings);
          const { maxCleaners: maxPendingCleaners } = getBusiestHourSummary(pending);

          return (
            <div key={index} className="day-row">
              <div className="date-column sticky">
                <h3 className='bookings-date'>
                  <Link to={`/bookings/scheduling/${formattedDate}`} className="date-link">
                    {day.toFormat('ccc d LLL')}
                  </Link>
                </h3>
                <div className="icon-summary">
                  <span className="icon-item"><FontAwesomeIcon icon={faCalendarAlt} className="minimal-icon" /> {summaryBookings.length}</span>
                  <span className="icon-item"><FontAwesomeIcon icon={faUsers} className="minimal-icon" /> {maxCleaners}</span>
                </div>
                {pending.length > 0 && (
                  <div className="icon-summary pending-summary">
                    <span className="icon-item"><FontAwesomeIcon icon={faCalendarAlt} className="minimal-icon" /> {pending.length}</span>
                    <span className="icon-item"><FontAwesomeIcon icon={faUsers} className="minimal-icon" /> {maxPendingCleaners}</span>
                  </div>
                )}
              </div>
              <div className="bookings-columns">
                {sections.map((section, idx) => (
                  <React.Fragment key={idx}>
                    {section.data.length > 0 && idx > 0 &&
                      sections.slice(0, idx).some(s => s.data.length > 0) && (
                        <div className="separator"></div>
                      )}
                    {section.data.map((booking, i) => {
                      const dtStart = DateTime.fromISO(booking.booking_start, { zone: 'utc' }).setZone(DUBAI);
                      const dtEnd = DateTime.fromISO(booking.booking_end, { zone: 'utc' }).setZone(DUBAI);
                      const cleanerNames = booking.cleaner_names || 'No cleaners assigned';
                      const maintLabel = maintenanceCardLabel(booking.booking_type);
                      const areaColor = getAreaColor(booking.area);

                      let dynamicBadge = section.badge;
                      if (!dynamicBadge && booking.status_name === 'Last Minute Cancelled') dynamicBadge = 'Cancelled';
                      if (!dynamicBadge && booking.status_name === 'Last Minute Rescheduled') dynamicBadge = 'Rescheduled';
                      if (!dynamicBadge && booking.booking_type === 'Check-in Assistance') dynamicBadge = 'Check-in';

                      const badgeClass = {
                        'Cancelled': 'cancelled-badge',
                        'Rescheduled': 'rescheduled-badge',
                        'Pending': 'pending-badge',
                        'Laundry Only': 'laundry-badge',
                        'Delivery': 'delivery-badge',
                        'Pickup': 'pickup-badge',
                        'Check-in': 'checkin-badge',
                        'Porter Delivery': 'delivery-badge',
                        'Maintenance': 'maintenance-badge',
                        'Swimming Pool Maintenance': 'maintenance-badge',
                      }[dynamicBadge] || '';

                      return (
                        <Link
                          key={booking.id}
                          className={`booking-box ${section.className}`}
                          style={{ backgroundColor: areaColor, opacity: dynamicBadge ? 0.7 : 1, cursor: 'pointer' }}
                          to={`/bookings/${booking.id}`}
                          data-tooltip-id={`booking-${booking.id}`}
                          data-tooltip-content={
                            `Client: ${booking.client_name}\nUnit: ${booking.unit_number} - ${booking.building_name}` +
                            (booking.bedrooms ? `\nBedrooms: ${booking.bedrooms}` : '') +
                            (booking.booking_type ? `\nType: ${booking.booking_type}` : '') +
                            (String(booking.status_name).trim().toLowerCase() === 'pending' && booking.pending_reason
                              ? `\nPending Reason: ${booking.pending_reason}`
                              : '') +
                            `\n\nTime: ${dtStart.toFormat('HH:mm')} - ${dtEnd.toFormat('HH:mm')}` +
                            `\nCleaners: ${maintLabel ? `not needed (${maintLabel})` : cleanerNames}`
                          }
                        >
                          {dynamicBadge && <span className={badgeClass}>{dynamicBadge}</span>}
                          <p className="booking-number">{section.label} {i + 1}</p>
                          <p className="property-info">
                            <strong>{booking.unit_number} - {booking.building_name}</strong>
                          </p>
                          <p className="client-name">{booking.client_name}</p>
                          <p className="booking-time">
                            {dtStart.toFormat('HH:mm')} - {dtEnd.toFormat('HH:mm')}
                          </p>
                          {maintLabel ? (
                            <p className="cleaners-required cleaners-required--none" title="No cleaners needed">
                              <span className="type-pill">{maintLabel}</span>
                            </p>
                          ) : (
                            <p className="cleaners-required">Cleaners: {booking.required_cleaners}</p>
                          )}
                        </Link>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Tooltip anchorSelect="[data-tooltip-id]" place="top" className="custom-tooltip" />
    </div>
  );
}

export default Bookings;
